import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard } from '@backstage/core-components';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import AssessmentIcon from '@material-ui/icons/Assessment';
import ArticleIcon from '@material-ui/icons/Description';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

import {
  ENV_LABEL,
  entityEnvironments,
  envLinks,
  resolveNamespace,
} from '../catalog/appEnvironments';

// Renders "Open in Grafana" deep links on the entity Overview: Pod Metrics
// and Logs for each environment the app runs in (`sparrow.io/environments`;
// staging-only when absent). Each link is a normal <a target="_blank"> —
// the full Grafana feature set is one click away.
//
// Pod Metrics → the k8s-pod-metrics dashboard on the metrics Grafana, with
// var-datasource / var-cluster for the BU + environment (from
// `sparrow.<bu>.<env>` config), var-namespace = the app's namespace and
// var-deployment = <namespace>-application (bootstrap convention).
// Logs → the app-logs dashboard (or Grafana Explore where that Grafana has
// no app-logs dashboard) scoped to the namespace. See appEnvironments.ts.

export const EntityObservabilityCard = () => {
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const namespace = resolveNamespace(entity);
  const envs = entityEnvironments(entity);

  const links: Array<{ label: string; href: string; icon: React.ReactNode }> = [];
  for (const env of envs) {
    const { metrics, logs } = envLinks(config, entity, env);
    const suffix = envs.length > 1 ? ` · ${ENV_LABEL[env]}` : '';
    if (metrics) {
      links.push({ label: `Pod Metrics${suffix}`, href: metrics, icon: <AssessmentIcon /> });
    }
    if (logs) {
      links.push({ label: `Logs${suffix}`, href: logs, icon: <ArticleIcon /> });
    }
  }

  if (links.length === 0) {
    return null; // nothing configured — don't render an empty card
  }

  return (
    <InfoCard title="Observability">
      <List dense>
        {links.map(link => (
          <ListItem
            key={link.label}
            button
            component="a"
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ListItemIcon>{link.icon}</ListItemIcon>
            <ListItemText
              primary={link.label}
              secondary={`Open in Grafana → scoped to ${namespace}`}
            />
            <OpenInNewIcon fontSize="small" color="action" />
          </ListItem>
        ))}
      </List>
    </InfoCard>
  );
};

export default EntityObservabilityCard;
